import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Utensils, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Meal } from "../../types";
import apiService from "../../services/api";
import telegramService from "../../utils/telegram";

interface TodayMealsProps {
  meals: Meal[];
  onChanged: () => void;
}

const MealRow = React.memo(function MealRow({
  meal,
  index,
  onDelete,
}: {
  meal: Meal;
  index: number;
  onDelete: (mealId: string) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ delay: 0.1 * index }}
      layout
      className="card p-3 flex items-center gap-3"
    >
      <div className="w-14 h-14 rounded-2xl bg-brand-soft shrink-0 overflow-hidden relative shadow-card">
        {meal.imageUrl ? (
          <img
            src={meal.imageUrl}
            className="w-full h-full object-cover"
            alt={meal.name}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl">🍽️</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-content-primary truncate">{meal.name}</h4>
        <div className="flex items-center gap-3 text-xs text-content-tertiary mt-1">
          <span className="flex items-center gap-1">
            <Clock size={10} />{" "}
            {new Date(meal.timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          <span className="font-bold text-brand-text bg-brand-soft px-1.5 rounded-md">
            {Math.round(meal.calories)} kcal
          </span>
        </div>
      </div>
      <button
        onClick={() => onDelete(meal._id)}
        aria-label="Delete"
        className="p-2 text-content-tertiary hover:text-danger hover:bg-danger-soft rounded-full transition-all"
      >
        <Trash2 size={18} />
      </button>
    </motion.div>
  );
});

const TodayMeals: React.FC<TodayMealsProps> = ({ meals, onChanged }) => {
  const { t } = useTranslation();

  const handleDelete = async (mealId: string) => {
    telegramService.haptic("light");
    const confirmed = await telegramService.showConfirm(t("mealHistory.deleteConfirm"));
    if (!confirmed) return;
    const tgId = telegramService.getUserId();
    if (!tgId) return;
    try {
      await apiService.deleteMeal(mealId, tgId);
      telegramService.haptic("success");
      onChanged();
    } catch (error) {
      console.error("Error deleting meal:", error);
      telegramService.haptic("error");
    }
  };

  return (
    <div className="px-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-content-primary flex items-center gap-2">
          {t("home.recentMeals")}{" "}
          <span className="text-xs font-normal text-content-tertiary bg-inset px-2 py-0.5 rounded-full">
            {meals.length}
          </span>
        </h2>
      </div>

      <div className="space-y-3">
        <AnimatePresence>
          {meals.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-8 text-center text-content-tertiary bg-surface-elevated rounded-3xl border border-dashed border-brand-soft-2"
            >
              <Utensils className="mx-auto mb-2 opacity-50" size={32} />
              <p className="text-sm font-medium">{t("home.noMeals")}</p>
            </motion.div>
          ) : (
            meals.map((meal, index) => (
              <MealRow key={meal._id} meal={meal} index={index} onDelete={handleDelete} />
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TodayMeals;
